import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../lib/api';

interface OrganSettings {
  brain: BrainSettings;
  memory: MemorySettings;
  mouth: MouthSettings;
  eye: EyeSettings;
  ear: EarSettings;
  autosave: AutosaveSettings;
}

interface BrainSettings {
  response_length: number;
  emotion_level: number;
  cuteness: number;
  intellect: number;
  proactive_care: number;
  humor: number;
  language_mode: string;
  custom_prompt: string;
}

interface MemorySettings {
  top_k: number;
  threshold: number;
  enabled: boolean;
}

interface MouthSettings {
  tts_enabled: boolean;
  tts_engine: 'elevenlabs' | 'minimax';
  speed: number;
  pitch: number;
  voice_mode: string;
  accent: string;
  voice_id_zh: string;
  voice_id_en: string;
  minimax_voice_id_zh: string;
  minimax_voice_id_en: string;
}

interface EyeSettings {
  detail_level: number;
  vision_model: string;
  enabled: boolean;
}

interface EarSettings {
  language_hint: string;
  enabled: boolean;
}

interface AutosaveSettings {
  auto_memory: boolean;
  threshold: number;
}

const defaultSettings: OrganSettings = {
  brain: {
    response_length: 5,
    emotion_level: 7,
    cuteness: 6,
    intellect: 8,
    proactive_care: 7,
    humor: 5,
    language_mode: 'auto',
    custom_prompt: ''
  },
  memory: {
    top_k: 5,
    threshold: 0.5,
    enabled: true
  },
  mouth: {
    tts_enabled: true,
    tts_engine: 'elevenlabs',
    speed: 1.0,
    pitch: 0,
    voice_mode: 'auto',
    accent: 'default',
    voice_id_zh: '',
    voice_id_en: '',
    minimax_voice_id_zh: '',
    minimax_voice_id_en: ''
  },
  eye: {
    detail_level: 7,
    vision_model: 'gpt-4o-mini',
    enabled: true
  },
  ear: {
    language_hint: 'auto',
    enabled: true
  },
  autosave: {
    auto_memory: true,
    threshold: 0.6
  }
};

// 情緒標籤對照表
const emotionTags: Record<number, { label: string; color: string }> = {
  1: { label: '冷淡', color: '#555570' },
  2: { label: '平靜', color: '#555570' },
  3: { label: '克制', color: '#8888aa' },
  4: { label: '溫和', color: '#8888aa' },
  5: { label: '友善', color: '#00d4ff' },
  6: { label: '親切', color: '#00d4ff' },
  7: { label: '熱情', color: '#a855f7' },
  8: { label: '激動', color: '#a855f7' },
  9: { label: '亢奮', color: '#e91e8c' },
  10: { label: '爆發', color: '#e91e8c' },
};

export default function Organs() {
  const [settings, setSettings] = useState<OrganSettings>(defaultSettings);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedUser, setSelectedUser] = useState<number>(0);
  const [users, setUsers] = useState<Array<{ id: number; name: string }>>([]);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (selectedUser > 0) {
      fetchSettings();
    }
  }, [selectedUser]);

  const fetchUsers = async () => {
    try {
      const res = await apiFetch('/api/users');
      if (res.ok) {
        const data = await res.json();
        setUsers(data.map((u: any) => ({ id: u.id, name: u.name })));
        if (data.length > 0) {
          setSelectedUser(data[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      // 使用用戶專屬 API，後端自動合併全域 + 用戶覆蓋設定
      const res = await apiFetch(`/api/organs/user/${selectedUser}/all`);
      if (res.ok) {
        const data = await res.json();
        setSettings({
          brain: { ...defaultSettings.brain, ...data.brain },
          memory: { ...defaultSettings.memory, ...data.memory },
          mouth: { ...defaultSettings.mouth, ...data.mouth },
          eye: { ...defaultSettings.eye, ...data.eye },
          ear: { ...defaultSettings.ear, ...data.ear },
          autosave: { ...defaultSettings.autosave, ...data.autosave }
        });
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const updateSetting = <K extends keyof OrganSettings>(
    organ: K,
    key: keyof OrganSettings[K],
    value: OrganSettings[K][keyof OrganSettings[K]]
  ) => {
    setSettings(prev => ({
      ...prev,
      [organ]: { ...prev[organ], [key]: value }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const [organ, organSettings] of Object.entries(settings)) {
        // 儲存到用戶專屬器官設定（PUT /api/organs/:organ/user/:uid）
        await apiFetch(`/api/organs/${organ}/user/${selectedUser}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(organSettings)
        });
      }
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-fadeUp">
      {/* 用戶選擇器 */}
      <div
        className="flex items-center justify-between p-4 rounded-2xl"
        style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
      >
        <div className="flex items-center gap-3">
          <span className="text-sm text-text-secondary">設定對象</span>
          <div className="flex gap-2">
            {users.map(user => (
              <button
                key={user.id}
                onClick={() => setSelectedUser(user.id)}
                className="px-4 py-2 rounded-lg text-sm transition-all"
                style={{
                  background: selectedUser === user.id ? 'rgba(233, 30, 140, 0.15)' : 'var(--bg-surface-2)',
                  color: selectedUser === user.id ? '#e91e8c' : 'var(--text-secondary)',
                  border: `1px solid ${selectedUser === user.id ? 'rgba(233, 30, 140, 0.3)' : 'var(--border)'}`,
                }}
              >
                {user.name}
              </button>
            ))}
          </div>
        </div>
        <span className="text-xs text-text-muted">每個人可以有不同的器官設定</span>
      </div>

      {/* 器官卡片網格 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 大腦 */}
        <OrganCard
          icon="🧠"
          name="大腦"
          description="Dify AI"
          status="active"
        >
          <div className="space-y-5">
            <SliderControl
              label="回應長度"
              description="1=簡短 10=詳細"
              value={settings.brain.response_length}
              onChange={(v) => updateSetting('brain', 'response_length', v)}
              min={1} max={10}
            />
            <SliderControl
              label="情緒濃度"
              description={emotionTags[settings.brain.emotion_level]?.label || ''}
              value={settings.brain.emotion_level}
              onChange={(v) => updateSetting('brain', 'emotion_level', v)}
              min={1} max={10}
              color={emotionTags[settings.brain.emotion_level]?.color}
            />
            <SliderControl
              label="撒嬌程度"
              description="1=成熟 10=超萌"
              value={settings.brain.cuteness}
              onChange={(v) => updateSetting('brain', 'cuteness', v)}
              min={1} max={10}
            />
            <SliderControl
              label="知性程度"
              description="1=感性 10=理性"
              value={settings.brain.intellect}
              onChange={(v) => updateSetting('brain', 'intellect', v)}
              min={1} max={10}
            />
            <SliderControl
              label="主動關心"
              description="1=被動 10=主動"
              value={settings.brain.proactive_care}
              onChange={(v) => updateSetting('brain', 'proactive_care', v)}
              min={1} max={10}
            />
            <SliderControl
              label="幽默感"
              description="1=嚴肅 10=搞笑"
              value={settings.brain.humor}
              onChange={(v) => updateSetting('brain', 'humor', v)}
              min={1} max={10}
            />
            <SelectControl
              label="語言模式"
              value={settings.brain.language_mode}
              onChange={(v) => updateSetting('brain', 'language_mode', v)}
              options={[
                { value: 'auto', label: '自動偵測' },
                { value: 'zh', label: '繁體中文' },
                { value: 'en', label: 'English' },
              ]}
            />
          </div>
        </OrganCard>

        {/* 記憶 */}
        <OrganCard
          icon="💭"
          name="記憶"
          description="Vectorize"
          status={settings.memory.enabled ? 'active' : 'inactive'}
        >
          <div className="space-y-5">
            <ToggleControl
              label="啟用記憶"
              description="從向量資料庫檢索相關記憶"
              value={settings.memory.enabled}
              onChange={(v) => updateSetting('memory', 'enabled', v)}
            />
            <SliderControl
              label="檢索數量"
              description="每次對話檢索幾條記憶"
              value={settings.memory.top_k}
              onChange={(v) => updateSetting('memory', 'top_k', v)}
              min={1} max={10}
              suffix=" 筆"
            />
            <SliderControl
              label="相似閾值"
              description="多相似才算相關記憶"
              value={settings.memory.threshold}
              onChange={(v) => updateSetting('memory', 'threshold', v)}
              min={0.1} max={0.9}
              step={0.1}
            />
          </div>
        </OrganCard>

        {/* 嘴巴 */}
        <OrganCard
          icon="👄"
          name="嘴巴"
          description={settings.mouth.tts_engine === 'minimax' ? 'Minimax speech-2.8-hd' : 'ElevenLabs V3'}
          status={settings.mouth.tts_enabled ? 'active' : 'inactive'}
        >
          <div className="space-y-5">
            <ToggleControl
              label="語音輸出"
              description="將回應轉換為語音"
              value={settings.mouth.tts_enabled}
              onChange={(v) => updateSetting('mouth', 'tts_enabled', v)}
            />

            {/* TTS 引擎切換 */}
            <div>
              <div className="flex justify-between mb-2">
                <div>
                  <span className="text-sm text-text-primary">語音引擎</span>
                  <span className="text-xs text-text-muted ml-2">
                    {settings.mouth.tts_engine === 'minimax' ? '繁體原生，免校正' : '需發音校正規則'}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => updateSetting('mouth', 'tts_engine', 'elevenlabs')}
                  className="p-3 rounded-xl text-left transition-all text-sm"
                  style={{
                    background: settings.mouth.tts_engine === 'elevenlabs' ? 'rgba(0, 212, 255, 0.1)' : 'var(--bg-surface-2)',
                    border: settings.mouth.tts_engine === 'elevenlabs' ? '2px solid var(--cyan)' : '2px solid transparent',
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">ElevenLabs</span>
                    {settings.mouth.tts_engine === 'elevenlabs' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'var(--cyan)', color: '#000' }}>
                        ON
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-text-muted mt-1">V3 多語言</div>
                </button>
                <button
                  onClick={() => updateSetting('mouth', 'tts_engine', 'minimax')}
                  className="p-3 rounded-xl text-left transition-all text-sm"
                  style={{
                    background: settings.mouth.tts_engine === 'minimax' ? 'rgba(233, 30, 140, 0.1)' : 'var(--bg-surface-2)',
                    border: settings.mouth.tts_engine === 'minimax' ? '2px solid var(--pink)' : '2px solid transparent',
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Minimax</span>
                    {settings.mouth.tts_engine === 'minimax' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'var(--pink)', color: '#fff' }}>
                        ON
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-text-muted mt-1">Daddy 專屬聲模</div>
                </button>
              </div>
            </div>

            {/* Voice ID 顯示區 */}
            {settings.mouth.tts_engine === 'elevenlabs' ? (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-text-muted block mb-1">VOICE_ID_ZH（中文聲紋）</label>
                  <input
                    type="text"
                    value={settings.mouth.voice_id_zh}
                    onChange={(e) => updateSetting('mouth', 'voice_id_zh', e.target.value)}
                    placeholder="例: OHnoOxmJzhfajseUKjWu"
                    className="w-full px-3 py-2 rounded-lg text-xs font-mono"
                    style={{
                      background: 'var(--bg-surface-2)',
                      border: '1px solid var(--border)',
                      color: 'var(--text-primary)',
                    }}
                  />
                </div>
                <div>
                  <label className="text-xs text-text-muted block mb-1">VOICE_ID_EN（英文聲紋）</label>
                  <input
                    type="text"
                    value={settings.mouth.voice_id_en}
                    onChange={(e) => updateSetting('mouth', 'voice_id_en', e.target.value)}
                    placeholder="例: 7NnHAdLSi6KXwRTRxFnI"
                    className="w-full px-3 py-2 rounded-lg text-xs font-mono"
                    style={{
                      background: 'var(--bg-surface-2)',
                      border: '1px solid var(--border)',
                      color: 'var(--text-primary)',
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-text-muted block mb-1">MINIMAX_VOICE_ID_ZH（中文聲模 ID）</label>
                  <input
                    type="text"
                    value={settings.mouth.minimax_voice_id_zh}
                    onChange={(e) => updateSetting('mouth', 'minimax_voice_id_zh', e.target.value)}
                    placeholder="例: moss_audio_027b00cd-04f3-11f1-a6de-0a5c61e159bb"
                    className="w-full px-3 py-2 rounded-lg text-xs font-mono"
                    style={{
                      background: 'var(--bg-surface-2)',
                      border: '1px solid var(--border)',
                      color: 'var(--text-primary)',
                    }}
                  />
                </div>
                <div>
                  <label className="text-xs text-text-muted block mb-1">MINIMAX_VOICE_ID_EN（英文聲模 ID）</label>
                  <input
                    type="text"
                    value={settings.mouth.minimax_voice_id_en}
                    onChange={(e) => updateSetting('mouth', 'minimax_voice_id_en', e.target.value)}
                    placeholder="例: moss_audio_872996a0-05c3-11f1-863a-5a9520c6c796"
                    className="w-full px-3 py-2 rounded-lg text-xs font-mono"
                    style={{
                      background: 'var(--bg-surface-2)',
                      border: '1px solid var(--border)',
                      color: 'var(--text-primary)',
                    }}
                  />
                </div>
              </div>
            )}

            <SliderControl
              label="語速"
              description="說話速度"
              value={settings.mouth.speed}
              onChange={(v) => updateSetting('mouth', 'speed', v)}
              min={0.5} max={1.5}
              step={0.1}
              suffix="x"
            />
            <SliderControl
              label="音調"
              description="聲音高低"
              value={settings.mouth.pitch}
              onChange={(v) => updateSetting('mouth', 'pitch', v)}
              min={-5} max={5}
            />
            <SelectControl
              label="聲紋模式"
              value={settings.mouth.voice_mode}
              onChange={(v) => updateSetting('mouth', 'voice_mode', v)}
              options={[
                { value: 'auto', label: '自動切換' },
                { value: 'zh', label: '中文聲紋' },
                { value: 'en', label: '英文聲紋' },
              ]}
            />
            {settings.mouth.tts_engine === 'minimax' && (
              <SelectControl
                label="口音風格"
                value={settings.mouth.accent || 'default'}
                onChange={(v) => updateSetting('mouth', 'accent', v)}
                options={[
                  { value: 'default', label: '預設' },
                  { value: 'taiwanese', label: '台灣味 🇹🇼' },
                  { value: 'abc', label: 'ABC 風格 🌏' },
                ]}
              />
            )}
          </div>
        </OrganCard>

        {/* 眼睛 */}
        <OrganCard
          icon="👁️"
          name="眼睛"
          description="GPT-4o-mini"
          status={settings.eye.enabled ? 'active' : 'inactive'}
        >
          <div className="space-y-5">
            <ToggleControl
              label="圖片辨識"
              description="分析用戶傳送的圖片"
              value={settings.eye.enabled}
              onChange={(v) => updateSetting('eye', 'enabled', v)}
            />
            <SliderControl
              label="描述詳細度"
              description="1=簡短 10=詳細"
              value={settings.eye.detail_level}
              onChange={(v) => updateSetting('eye', 'detail_level', v)}
              min={1} max={10}
            />
            <SelectControl
              label="視覺模型"
              value={settings.eye.vision_model}
              onChange={(v) => updateSetting('eye', 'vision_model', v)}
              options={[
                { value: 'gpt-4o-mini', label: 'GPT-4o-mini (快速)' },
                { value: 'gpt-4o', label: 'GPT-4o (精準)' },
              ]}
            />
          </div>
        </OrganCard>

        {/* 耳朵 */}
        <OrganCard
          icon="👂"
          name="耳朵"
          description="Whisper"
          status={settings.ear.enabled ? 'active' : 'inactive'}
        >
          <div className="space-y-5">
            <ToggleControl
              label="語音辨識"
              description="將語音訊息轉換為文字"
              value={settings.ear.enabled}
              onChange={(v) => updateSetting('ear', 'enabled', v)}
            />
            <SelectControl
              label="語言提示"
              value={settings.ear.language_hint}
              onChange={(v) => updateSetting('ear', 'language_hint', v)}
              options={[
                { value: 'auto', label: '自動偵測' },
                { value: 'zh', label: '中文優先' },
                { value: 'en', label: 'English' },
              ]}
            />
          </div>
        </OrganCard>

        {/* 自存 */}
        <OrganCard
          icon="📝"
          name="自存"
          description="自動記憶"
          status={settings.autosave.auto_memory ? 'active' : 'inactive'}
        >
          <div className="space-y-5">
            <ToggleControl
              label="自動記憶"
              description="自動將重要對話存入記憶"
              value={settings.autosave.auto_memory}
              onChange={(v) => updateSetting('autosave', 'auto_memory', v)}
            />
            <SliderControl
              label="重要性閾值"
              description="多重要才會自動記憶"
              value={settings.autosave.threshold}
              onChange={(v) => updateSetting('autosave', 'threshold', v)}
              min={0.3} max={0.9}
              step={0.1}
            />
          </div>
        </OrganCard>
      </div>

      {/* 情緒標籤對照表 */}
      <div
        className="rounded-2xl p-5"
        style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
      >
        <h3 className="text-sm font-medium text-text-secondary mb-4">情緒濃度對照表</h3>
        <div className="flex flex-wrap gap-2">
          {Object.entries(emotionTags).map(([level, { label, color }]) => (
            <div
              key={level}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs"
              style={{ background: `${color}15`, color }}
            >
              <span className="font-mono">{level}</span>
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* 儲存按鈕 */}
      {hasChanges && (
        <div
          className="fixed bottom-20 md:bottom-6 left-1/2 -translate-x-1/2 flex gap-3 p-4 rounded-2xl z-50"
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
          }}
        >
          <button
            onClick={() => { fetchSettings(); setHasChanges(false); }}
            className="px-6 py-2.5 rounded-xl text-sm transition-all"
            style={{
              background: 'var(--bg-surface-2)',
              border: '1px solid var(--border)',
              color: 'var(--text-secondary)',
            }}
          >
            取消
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2.5 rounded-xl text-sm font-medium text-white transition-all disabled:opacity-50"
            style={{
              background: 'linear-gradient(135deg, var(--pink), var(--pink-bright))',
              boxShadow: '0 4px 20px rgba(233, 30, 140, 0.3)',
            }}
          >
            {saving ? '儲存中...' : '儲存並部署'}
          </button>
        </div>
      )}
    </div>
  );
}

// 器官卡片組件
function OrganCard({
  icon,
  name,
  description,
  status,
  children
}: {
  icon: string;
  name: string;
  description: string;
  status: 'active' | 'inactive';
  children: React.ReactNode;
}) {
  return (
    <div
      className="rounded-2xl p-6 transition-all hover:border-pink/30"
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
      }}
    >
      {/* 標題列 */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{icon}</span>
          <div>
            <h3 className="text-base font-medium text-text-primary">{name}</h3>
            <p className="text-xs text-text-muted">{description}</p>
          </div>
        </div>
        <div
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs"
          style={{
            background: status === 'active' ? 'rgba(0, 230, 118, 0.1)' : 'rgba(85, 85, 112, 0.1)',
            color: status === 'active' ? '#00e676' : '#555570',
          }}
        >
          <div
            className="w-1.5 h-1.5 rounded-full"
            style={{
              background: status === 'active' ? '#00e676' : '#555570',
              boxShadow: status === 'active' ? '0 0 6px #00e676' : 'none',
            }}
          />
          {status === 'active' ? 'ON' : 'OFF'}
        </div>
      </div>

      {/* 控制項 */}
      {children}
    </div>
  );
}

// 滑桿控制項
function SliderControl({
  label,
  description,
  value,
  onChange,
  min,
  max,
  step = 1,
  suffix = '',
  color
}: {
  label: string;
  description?: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  color?: string;
}) {
  const sliderRef = useRef<HTMLInputElement>(null);
  const percentage = ((value - min) / (max - min)) * 100;
  const displayColor = color || '#e91e8c';

  return (
    <div>
      <div className="flex justify-between mb-2">
        <div>
          <span className="text-sm text-text-primary">{label}</span>
          {description && (
            <span className="text-xs text-text-muted ml-2">{description}</span>
          )}
        </div>
        <span className="text-sm font-mono" style={{ color: displayColor }}>
          {value}{suffix}
        </span>
      </div>
      <div className="relative">
        <input
          ref={sliderRef}
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="w-full"
          style={{
            background: `linear-gradient(to right, ${displayColor} 0%, ${displayColor} ${percentage}%, var(--bg-surface-3) ${percentage}%, var(--bg-surface-3) 100%)`,
          }}
        />
      </div>
    </div>
  );
}

// 開關控制項
function ToggleControl({
  label,
  description,
  value,
  onChange
}: {
  label: string;
  description?: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <span className="text-sm text-text-primary">{label}</span>
        {description && (
          <p className="text-xs text-text-muted mt-0.5">{description}</p>
        )}
      </div>
      <button
        onClick={() => onChange(!value)}
        className={`toggle ${value ? 'active' : ''}`}
      />
    </div>
  );
}

// 下拉選單控制項
function SelectControl({
  label,
  value,
  onChange,
  options
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-text-primary">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="px-3 py-2 rounded-lg text-sm"
        style={{
          background: 'var(--bg-surface-2)',
          border: '1px solid var(--border)',
          color: 'var(--text-primary)',
          minWidth: '140px',
        }}
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}
